import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaUserMd, FaClock, FaCopy, FaCalendarCheck } from 'react-icons/fa';
import { apiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { Provider } from '../../types/provider';

interface AppointmentCategory {
    id: number | string;
    name: string;
    code: string;
}

interface CategoryOption {
    value: number | string;
    label: string;
    code: string;
}

interface ConsultingAppointmentType {
    id: number | string;
    code?: string;
    name: string;
    description?: string;
    categories?: (number | string)[];
    category_codes?: string[];
    requires_anaesthetist?: boolean;
    default_duration_minutes?: number;
    estimated_fee?: string | number;
    provider?: number | string;
    provider_name?: string;
    laterality?: string;
    body_area?: string;
    requires_referral?: boolean;
    requires_consent?: boolean;
    requires_theatre?: boolean;
    allow_online_booking?: boolean;
    buffer_time_minutes?: number;
    medicare_item_numbers?: string;
    estimated_cost?: string | number;
    is_active?: boolean;
    appointment_kind?: string;
}

interface AppointmentTypeFormData {
    code: string;
    name: string;
    description: string;
    categories: (number | string)[];
    requires_anaesthetist: boolean;
    default_duration_minutes: number;
    estimated_fee: string;
    provider: number | string;
    laterality: string;
    body_area: string;
    requires_referral: boolean;
    requires_consent: boolean;
    requires_theatre: boolean;
    allow_online_booking: boolean;
    buffer_time_minutes: number;
    medicare_item_numbers: string;
    estimated_cost: string;
    is_active: boolean;
}

const LATERALITY_CHOICES = [
    { value: 'left', label: 'Left' },
    { value: 'right', label: 'Right' },
    { value: 'bilateral', label: 'Bilateral' },
    { value: 'na', label: 'N/A' }
];

const BODY_AREA_CHOICES = [
    { value: '', label: 'Not Set' },
    { value: 'head', label: 'Head' },
    { value: 'face', label: 'Face' },
    { value: 'neck', label: 'Neck' },
    { value: 'shoulder', label: 'Shoulder' },
    { value: 'upper_arm', label: 'Upper Arm' },
    { value: 'elbow', label: 'Elbow' },
    { value: 'forearm', label: 'Forearm' },
    { value: 'wrist', label: 'Wrist' },
    { value: 'hand', label: 'Hand' },
    { value: 'chest', label: 'Chest' },
    { value: 'upper_back', label: 'Upper Back (Thoracic)' },
    { value: 'lower_back', label: 'Lower Back' },
    { value: 'abdomen', label: 'Abdomen' },
    { value: 'pelvis_groin', label: 'Pelvis/Groin' },
    { value: 'buttocks', label: 'Buttocks' },
    { value: 'hip', label: 'Hip' },
    { value: 'thigh', label: 'Thigh' },
    { value: 'knee', label: 'Knee' },
    { value: 'shin', label: 'Shin' },
    { value: 'calf', label: 'Calf' },
    { value: 'ankle', label: 'Ankle' },
    { value: 'foot', label: 'Foot' }
];

const AppointmentTypes = () => {
    const { token } = useAuth();
    const [appointmentTypes, setAppointmentTypes] = useState<ConsultingAppointmentType[]>([]);
    const [providers, setProviders] = useState<Provider[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [editingAppointment, setEditingAppointment] = useState<ConsultingAppointmentType | null>(null);
    const [duplicatingAppointment, setDuplicatingAppointment] = useState<ConsultingAppointmentType | null>(null);
    const [selectedAppointmentKind, setSelectedAppointmentKind] = useState('all');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedProvider, setSelectedProvider] = useState('all');
    const [selectedLaterality, setSelectedLaterality] = useState('all');
    const [selectedBodyArea, setSelectedBodyArea] = useState('all');
    const [categories, setCategories] = useState<AppointmentCategory[]>([]);
    const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const itemsPerPage = 7;

    const [formData, setFormData] = useState<AppointmentTypeFormData>({
        code: '',
        name: '',
        description: '',
        categories: [],
        requires_anaesthetist: false,
        default_duration_minutes: 30,
        estimated_fee: '',
        provider: '',
        laterality: 'na',
        body_area: '',
        requires_referral: false,
        requires_consent: false,
        requires_theatre: false,
        allow_online_booking: true,
        buffer_time_minutes: 0,
        medicare_item_numbers: '',
        estimated_cost: '',
        is_active: true
    });

    const [duplicateFormData, setDuplicateFormData] = useState({
        target_provider_id: ''
    });

    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (!token) return;
        loadCategories();
        loadData();
    }, [token, selectedAppointmentKind, selectedCategory, selectedProvider, selectedLaterality, selectedBodyArea, currentPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedAppointmentKind, selectedCategory, selectedProvider, selectedLaterality, selectedBodyArea, searchTerm]);

    const loadCategories = async () => {
        if (!token) return;
        try {
            const response = await apiCall('/scheduling/appointment-type-categories/', { method: 'GET' }, token);
            if (response.ok) {
                const data = await response.json();
                const categoriesArray = data.results || data;
                setCategories(Array.isArray(categoriesArray) ? categoriesArray : []);
                setCategoryOptions(categoriesArray.map((cat: AppointmentCategory) => ({
                    value: cat.id,
                    label: cat.name,
                    code: cat.code
                })));
            }
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    };

    const loadData = async () => {
        if (!token) return;
        try {
            setLoading(true);
            
            // Build query parameters
            const params = new URLSearchParams();
            params.append('page', currentPage.toString());
            params.append('page_size', itemsPerPage.toString());
            
            if (selectedCategory && selectedCategory !== 'all') {
                params.append('categories', selectedCategory);
            }
            if (selectedProvider && selectedProvider !== 'all') {
                params.append('provider', selectedProvider);
            }
            if (selectedAppointmentKind && selectedAppointmentKind !== 'all') {
                params.append('appointment_kind', selectedAppointmentKind);
            }
            if (selectedLaterality && selectedLaterality !== 'all') {
                params.append('laterality', selectedLaterality);
            }
            if (selectedBodyArea && selectedBodyArea !== 'all') {
                params.append('body_area', selectedBodyArea);
            }
            if (searchTerm) {
                params.append('search', searchTerm);
            }

            // Fetch appointment types
            const response = await apiCall(`/scheduling/consulting-appointment-types/?${params}`, {
                method: 'GET'
            }, token);
            const data = await response.json();
            
            // Handle both paginated and non-paginated responses
            if (data.results) {
                setAppointmentTypes(data.results);
                setTotalCount(data.count || 0);
                setTotalPages(Math.ceil((data.count || 0) / itemsPerPage));
            } else {
                setAppointmentTypes(Array.isArray(data) ? data : []);
                setTotalCount(Array.isArray(data) ? data.length : 0);
                setTotalPages(1);
            }

            // Fetch providers
            const providersResponse = await apiCall('/providers/providers/', {
                method: 'GET'
            }, token);
            const providersData = await providersResponse.json();
            const allProviders = providersData.results || providersData;
            setProviders(Array.isArray(allProviders) ? allProviders : []);

        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token) return;

        try {
            const url = editingAppointment
                ? `/scheduling/consulting-appointment-types/${editingAppointment.id}/`
                : '/scheduling/consulting-appointment-types/';
            
            const method = editingAppointment ? 'PATCH' : 'POST';
            
            const response = await apiCall(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
            }, token);

            if (response.ok) {
                setShowModal(false);
                resetForm();
                loadData();
            } else {
                const errorData = await response.json();
                console.error('Error saving appointment type:', errorData);
                alert('Error saving appointment type: ' + JSON.stringify(errorData));
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error saving appointment type');
        }
    };

    const handleDuplicate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!duplicatingAppointment || !token) return;

        try {
            const response = await apiCall(
                `/scheduling/consulting-appointment-types/${duplicatingAppointment.id}/duplicate/`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(duplicateFormData)
                },
                token
            );

            if (response.ok) {
                setShowDuplicateModal(false);
                setDuplicatingAppointment(null);
                setDuplicateFormData({ target_provider_id: '' });
                loadData();
                alert('Appointment type duplicated successfully!');
            } else {
                const errorData = await response.json();
                alert('Error duplicating: ' + (errorData.error || JSON.stringify(errorData)));
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error duplicating appointment type');
        }
    };

    const handleDelete = async (id: number | string) => {
        if (!window.confirm('Are you sure you want to delete this appointment type?')) {
            return;
        }
        if (!token) return;

        try {
            const response = await apiCall(`/scheduling/consulting-appointment-types/${id}/`, {
                method: 'DELETE'
            }, token);

            if (response.ok) {
                loadData();
            } else {
                alert('Error deleting appointment type');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error deleting appointment type');
        }
    };

    const resetForm = () => {
        setFormData({
            code: '',
            name: '',
            description: '',
            categories: [],
            requires_anaesthetist: false,
            default_duration_minutes: 30,
            estimated_fee: '',
            provider: '',
            laterality: 'na',
            body_area: '',
            requires_referral: false,
            requires_consent: false,
            requires_theatre: false,
            allow_online_booking: true,
            buffer_time_minutes: 0,
            medicare_item_numbers: '',
            estimated_cost: '',
            is_active: true
        });
        setEditingAppointment(null);
    };

    const openEditModal = (appointment: ConsultingAppointmentType) => {
        setEditingAppointment(appointment);
        setFormData({
            code: appointment.code || '',
            name: appointment.name || '',
            description: appointment.description || '',
            categories: appointment.categories || [],
            requires_anaesthetist: appointment.requires_anaesthetist || false,
            default_duration_minutes: appointment.default_duration_minutes || 30,
            estimated_fee: appointment.estimated_fee?.toString() || '',
            provider: appointment.provider || '',
            laterality: appointment.laterality || 'na',
            body_area: appointment.body_area || '',
            requires_referral: appointment.requires_referral || false,
            requires_consent: appointment.requires_consent || false,
            requires_theatre: appointment.requires_theatre || false,
            allow_online_booking: appointment.allow_online_booking !== false,
            buffer_time_minutes: appointment.buffer_time_minutes || 0,
            medicare_item_numbers: appointment.medicare_item_numbers || '',
            estimated_cost: appointment.estimated_cost?.toString() || '',
            is_active: appointment.is_active !== false
        });
        setShowModal(true);
    };

    const openDuplicateModal = (appointment: ConsultingAppointmentType) => {
        setDuplicatingAppointment(appointment);
        setDuplicateFormData({ target_provider_id: '' });
        setShowDuplicateModal(true);
    };

    const getCategoryBadge = (code: string) => {
        switch(code) {
            case 'NC': return 'badge-success';
            case 'TC': return 'badge-info';
            case 'RC': return 'badge-warning';
            case 'PC': return 'badge-secondary';
            default: return 'badge-ghost';
        }
    };

    const filteredAppointments = appointmentTypes.filter(apt =>
        apt.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        apt.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <span className="loading loading-spinner loading-lg"></span>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
                    <FaCalendarCheck className="text-primary" />
                    Appointment Types
                </h1>
                <button
                    className="btn btn-primary"
                    onClick={() => {
                        resetForm();
                        setShowModal(true);
                    }}
                >
                    <FaPlus className="mr-2" />
                    Add Appointment Type
                </button>
            </div>

            {/* Filters */}
            <div className="card bg-base-100 shadow-md mb-6">
                <div className="card-body">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Type</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={selectedAppointmentKind}
                                onChange={(e) => setSelectedAppointmentKind(e.target.value)}
                            >
                                <option value="all">All</option>
                                <option value="appointments">Appointments</option>
                                <option value="procedures">Procedures</option>
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Search</span>
                            </label>
                            <input
                                type="text"
                                placeholder="Search appointment types..."
                                className="input input-bordered"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Category</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                            >
                                <option value="all">All Categories</option>
                                {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Provider</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={selectedProvider}
                                onChange={(e) => setSelectedProvider(e.target.value)}
                            >
                                <option value="all">All Providers</option>
                                {providers.map(provider => (
                                    <option key={provider.id} value={provider.id}>
                                        {provider.full_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Side</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={selectedLaterality}
                                onChange={(e) => setSelectedLaterality(e.target.value)}
                            >
                                <option value="all">All Sides</option>
                                {LATERALITY_CHOICES.map(choice => (
                                    <option key={choice.value} value={choice.value}>{choice.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Body Area</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={selectedBodyArea}
                                onChange={(e) => setSelectedBodyArea(e.target.value)}
                            >
                                <option value="all">All Body Areas</option>
                                {BODY_AREA_CHOICES.filter(area => area.value).map(choice => (
                                    <option key={choice.value} value={choice.value}>{choice.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Results count */}
            <div className="text-sm text-base-content/60 mb-4">
                Showing {filteredAppointments.length} of {totalCount} appointment types
            </div>

            {/* Appointment Types Table */}
            <div className="card bg-base-100 shadow-md">
                <div className="card-body p-0">
                    <div className="overflow-x-auto">
                        <table className="table table-zebra">
                            <thead>
                                <tr>
                                    <th>Code</th>
                                    <th>Name</th>
                                    <th>Type</th>
                                    <th>Provider</th>
                                    <th>Categories</th>
                                    <th>Duration</th>
                                    <th>Fee</th>
                                    <th>Status</th>
                                    <th className="text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAppointments.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="text-center py-8 text-base-content/60">
                                            No appointment types found
                                        </td>
                                    </tr>
                                ) : (
                                    filteredAppointments.map((appointment) => (
                                        <tr key={appointment.id}>
                                            <td>
                                                <span className="badge badge-outline">{appointment.code || '-'}</span>
                                            </td>
                                            <td>
                                                <div className="font-semibold">{appointment.name}</div>
                                                {appointment.description && (
                                                    <div className="text-sm text-base-content/60 max-w-md truncate">
                                                        {appointment.description}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`badge ${appointment.appointment_kind === 'procedure' ? 'badge-secondary' : 'badge-info'}`}>
                                                    {appointment.appointment_kind === 'procedure' ? 'Procedure' : 'Appointment'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="flex items-center gap-2">
                                                    <FaUserMd className="text-primary" />
                                                    {appointment.provider_name}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="flex flex-wrap gap-1">
                                                    {appointment.category_codes?.map((code, idx) => (
                                                        <span
                                                            key={idx}
                                                            className={`badge ${getCategoryBadge(code)} badge-sm px-3 py-3`}
                                                        >
                                                            {code}
                                                        </span>
                                                    ))}
                                                    {(!appointment.category_codes || appointment.category_codes.length === 0) && (
                                                        <span className="text-base-content/40">-</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="flex items-center gap-2">
                                                    <FaClock className="text-info" />
                                                    {appointment.default_duration_minutes} min
                                                </div>
                                            </td>
                                            <td>
                                                {appointment.estimated_fee
                                                    ? `$${parseFloat(appointment.estimated_fee.toString()).toFixed(2)}`
                                                    : '-'}
                                            </td>
                                            <td>
                                                <span className={`badge ${appointment.is_active ? 'badge-success' : 'badge-error'}`}>
                                                    {appointment.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="flex justify-center gap-2">
                                                    <button
                                                        className="btn btn-ghost btn-xs"
                                                        onClick={() => openEditModal(appointment)}
                                                        title="Edit"
                                                    >
                                                        <FaEdit />
                                                    </button>
                                                    <button
                                                        className="btn btn-ghost btn-xs"
                                                        onClick={() => openDuplicateModal(appointment)}
                                                        title="Duplicate"
                                                    >
                                                        <FaCopy />
                                                    </button>
                                                    <button
                                                        className="btn btn-ghost btn-xs text-error"
                                                        onClick={() => handleDelete(appointment.id)}
                                                        title="Delete"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex justify-center mt-6">
                    <div className="join">
                        <button
                            className="join-item btn"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        >
                            «
                        </button>
                        <button className="join-item btn">
                            Page {currentPage} of {totalPages}
                        </button>
                        <button
                            className="join-item btn"
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        >
                            »
                        </button>
                    </div>
                </div>
            )}

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-5xl max-h-[85vh]">
                        <h3 className="font-bold text-lg mb-4">
                            {editingAppointment ? 'Edit Appointment Type' : 'Add Appointment Type'}
                        </h3>
                        <form onSubmit={handleSubmit}>
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Code</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            value={formData.code}
                                            onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                                            placeholder="e.g. NC, NMC, NC-TELE"
                                        />
                                    </div>

                                    <div className="form-control sm:col-span-2">
                                        <label className="label">
                                            <span className="label-text">Name *</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            value={formData.name}
                                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                                            required
                                        />
                                    </div>

                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Provider *</span>
                                        </label>
                                        <select
                                            className="select select-bordered w-full"
                                            value={formData.provider}
                                            onChange={(e) => setFormData({...formData, provider: e.target.value})}
                                            required
                                        >
                                            <option value="">Select a provider...</option>
                                            {providers.map(provider => (
                                                <option key={provider.id} value={provider.id}>
                                                    {provider.full_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Description</span>
                                        </label>
                                        <textarea
                                            className="textarea textarea-bordered w-full"
                                            rows={3}
                                            value={formData.description}
                                            onChange={(e) => setFormData({...formData, description: e.target.value})}
                                        />
                                    </div>

                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Categories</span>
                                        </label>
                                        <div className="flex flex-wrap gap-2 border border-base-300 rounded-lg p-3 min-h-[4rem]">
                                            {categoryOptions.map(cat => (
                                                <label key={cat.value} className="cursor-pointer label p-0">
                                                    <input
                                                        type="checkbox"
                                                        className="checkbox checkbox-sm mr-2"
                                                        checked={formData.categories.includes(cat.value)}
                                                        onChange={(e) => {
                                                            const newCategories = e.target.checked
                                                                ? [...formData.categories, cat.value]
                                                                : formData.categories.filter(id => id !== cat.value);
                                                            setFormData({...formData, categories: newCategories});
                                                        }}
                                                    />
                                                    <span className={`badge ${getCategoryBadge(cat.code)}`}>
                                                        {cat.label}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Duration (min) *</span>
                                        </label>
                                        <input
                                            type="number"
                                            className="input input-bordered w-full"
                                            value={formData.default_duration_minutes}
                                            onChange={(e) => setFormData({...formData, default_duration_minutes: parseInt(e.target.value)})}
                                            required
                                            min="1"
                                        />
                                    </div>

                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Buffer Time (min)</span>
                                        </label>
                                        <input
                                            type="number"
                                            className="input input-bordered w-full"
                                            value={formData.buffer_time_minutes}
                                            onChange={(e) => setFormData({...formData, buffer_time_minutes: parseInt(e.target.value)})}
                                            min="0"
                                        />
                                    </div>

                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Estimated Fee</span>
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="input input-bordered w-full"
                                            value={formData.estimated_fee}
                                            onChange={(e) => setFormData({...formData, estimated_fee: e.target.value})}
                                            placeholder="0.00"
                                        />
                                    </div>

                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Estimated Cost</span>
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="input input-bordered w-full"
                                            value={formData.estimated_cost}
                                            onChange={(e) => setFormData({...formData, estimated_cost: e.target.value})}
                                            placeholder="0.00"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Medicare Item Numbers</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            value={formData.medicare_item_numbers}
                                            onChange={(e) => setFormData({...formData, medicare_item_numbers: e.target.value})}
                                            placeholder="e.g. 110, 116"
                                        />
                                    </div>

                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Side</span>
                                        </label>
                                        <select
                                            className="select select-bordered w-full"
                                            value={formData.laterality}
                                            onChange={(e) => setFormData({...formData, laterality: e.target.value})}
                                        >
                                            {LATERALITY_CHOICES.map(choice => (
                                                <option key={choice.value} value={choice.value}>{choice.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Body Area</span>
                                        </label>
                                        <select
                                            className="select select-bordered w-full"
                                            value={formData.body_area}
                                            onChange={(e) => setFormData({...formData, body_area: e.target.value})}
                                        >
                                            {BODY_AREA_CHOICES.map(choice => (
                                                <option key={choice.value || 'none'} value={choice.value}>{choice.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 border-t border-base-300 pt-4">
                                    <label className="cursor-pointer label justify-start gap-2 p-0">
                                        <input
                                            type="checkbox"
                                            className="checkbox"
                                            checked={formData.requires_anaesthetist}
                                            onChange={(e) => setFormData({...formData, requires_anaesthetist: e.target.checked})}
                                        />
                                        <span className="label-text">Requires Anaesthetist</span>
                                    </label>

                                    <label className="cursor-pointer label justify-start gap-2 p-0">
                                        <input
                                            type="checkbox"
                                            className="checkbox"
                                            checked={formData.requires_theatre}
                                            onChange={(e) => setFormData({...formData, requires_theatre: e.target.checked})}
                                        />
                                        <span className="label-text">Requires Theatre</span>
                                    </label>

                                    <label className="cursor-pointer label justify-start gap-2 p-0">
                                        <input
                                            type="checkbox"
                                            className="checkbox"
                                            checked={formData.requires_consent}
                                            onChange={(e) => setFormData({...formData, requires_consent: e.target.checked})}
                                        />
                                        <span className="label-text">Requires Consent</span>
                                    </label>

                                    <label className="cursor-pointer label justify-start gap-2 p-0">
                                        <input
                                            type="checkbox"
                                            className="checkbox"
                                            checked={formData.requires_referral}
                                            onChange={(e) => setFormData({...formData, requires_referral: e.target.checked})}
                                        />
                                        <span className="label-text">Requires Referral</span>
                                    </label>

                                    <label className="cursor-pointer label justify-start gap-2 p-0">
                                        <input
                                            type="checkbox"
                                            className="checkbox"
                                            checked={formData.allow_online_booking}
                                            onChange={(e) => setFormData({...formData, allow_online_booking: e.target.checked})}
                                        />
                                        <span className="label-text">Allow Online Booking</span>
                                    </label>

                                    <label className="cursor-pointer label justify-start gap-2 p-0">
                                        <input
                                            type="checkbox"
                                            className="checkbox"
                                            checked={formData.is_active}
                                            onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                                        />
                                        <span className="label-text">Active</span>
                                    </label>
                                </div>
                            </div>

                            <div className="modal-action">
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={() => {
                                        setShowModal(false);
                                        resetForm();
                                    }}
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {editingAppointment ? 'Update' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Duplicate Modal */}
            {showDuplicateModal && duplicatingAppointment && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg mb-4">
                            Duplicate Appointment Type
                        </h3>
                        <p className="mb-4">
                            Duplicating: <strong>{duplicatingAppointment.name}</strong>
                        </p>
                        <form onSubmit={handleDuplicate}>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Target Provider *</span>
                                </label>
                                <select
                                    className="select select-bordered"
                                    value={duplicateFormData.target_provider_id}
                                    onChange={(e) => setDuplicateFormData({...duplicateFormData, target_provider_id: e.target.value})}
                                    required
                                >
                                    <option value="">Select a provider...</option>
                                    {providers
                                        .filter(p => p.id !== duplicatingAppointment.provider)
                                        .map(provider => (
                                            <option key={provider.id} value={provider.id}>
                                                {provider.full_name}
                                            </option>
                                        ))}
                                </select>
                            </div>

                            <div className="modal-action">
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={() => {
                                        setShowDuplicateModal(false);
                                        setDuplicatingAppointment(null);
                                    }}
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Duplicate
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AppointmentTypes;
